import { Markdown } from 'tiptap-markdown'
import { Typography } from '@tiptap/extension-typography'
import { Extension, Extensions, Editor } from '@tiptap/core'
import { StarterKit } from '@tiptap/starter-kit'
import { Placeholder } from '@tiptap/extension-placeholder'
import {
  UpdatedImage,
  Blockquote,
  Kbd,
  BulletList,
  OrderedList,
  ListItem,
  TaskList,
  TaskItem,
  Bold,
  HorizontalRule,
  Heading,
} from '@basehub/tiptap-api-utils'
import { Paragraph } from '@tiptap/extension-paragraph'
import { CodeMark } from '~/lib/tiptap/extensions/rich-text/extensions/inline-mark'
import { Link } from '@tiptap/extension-link'
import { FakeCursor } from '~/lib/tiptap/extensions/rich-text/extensions/fake-cursor'
import { UploadImagesPlugin } from '~/lib/tiptap/plugins/upload-image'
import { Video } from '~/lib/tiptap/extensions/rich-text/extensions/video'
import { UploadVideosPlugin } from '~/lib/tiptap/plugins/upload-video'
import { Mention } from './mentions/user-mentions'
import { MentionsListProps } from './mentions/mentions-list'
import { PublicUser } from '~/app/api/users/public-user-schema'
import {
  BlockMention,
  BlockMentionPluginKey,
  BlockMentionProps,
} from './mentions/blocks-mention'
import { handleImageClickPlugin } from '~/lib/tiptap/plugins/handle-click-image'
import { TreeNode } from '@basehub/utils/tree/index'
import { Block } from '@basehub/data/replicache/index'
import { AiToolCall, AiToolResult, AiTool, AiError } from './ai-tools'
import { FileNode } from './file'
import { DeleteFirstEmptyParagraphExtension } from '~/lib/tiptap/extensions/rich-text/extensions/delete-first-empty-paragraph'

type FeedbackExtensionsOptions =
  | {
      handleEnter?: ({ editor }: { editor: Editor }) => void
      handleEscape?: ({ editor }: { editor: Editor }) => void
      getUsers?: (q: string | null) => MentionsListProps<PublicUser>['items']
      getBlocks?: (
        q: string | null
      ) => MentionsListProps<BlockMentionProps>['items']
      placeholder?: string
      mediaSize?: {
        width: number
        height: number
      }
      getNode?: (id: string) => TreeNode<Block> | null
      mentionHTMLAttributes?: Record<string, unknown>
    }
  | undefined
export const getFeedbackExtensions = (
  options?: FeedbackExtensionsOptions | undefined
) => {
  const {
    handleEnter,
    handleEscape,
    getUsers,
    placeholder,
    getBlocks,
    mediaSize,
    getNode,
    mentionHTMLAttributes,
  } = {
    handleEnter: () => {},
    handleEscape: () => {},
    getUsers: () => [],
    placeholder: '',
    getBlocks: () => [],
    mediaSize: undefined,
    getNode: () => null,
    ...options,
  }

  return [
    AiToolCall,
    AiToolResult,
    AiTool,
    AiError,
    FileNode,
    Paragraph.configure({
      HTMLAttributes: {
        class: 'rt-paragraph',
      },
    }),
    HorizontalRule.configure({
      HTMLAttributes: {
        class: 'rt-horizontal-rule',
      },
    }),
    Heading.extend({
      addInputRules() {
        // copiamos la regla original pero la envolvemos
        return [
          {
            find: /^(#+)\s$/,
            handler: ({ state, range, match, chain }) => {
              const mentionState = BlockMentionPluginKey.getState(state)

              const matched = match[1]
              if (!matched) return
              if (mentionState?.active && match[0]! !== '# ') {
                // ensure correct execution of mention plugin and no block the heading creation shortcut
                return
              }

              if (!match) return

              const level = Math.min(6, matched.length)

              chain()
                .focus()
                .deleteRange(range)
                .setNode('heading', { level })
                .run()
            },
          },
        ]
      },
    }).configure({
      HTMLAttributes: {
        class: 'rt-heading',
      },
    }),
    StarterKit.configure({
      horizontalRule: false,
      code: false,
      paragraph: false,
      dropcursor: { color: '#FF6C00', width: 2 },
      heading: false,
      codeBlock: {
        HTMLAttributes: {
          class: 'rt-codeblock',
        },
      },
      bold: false,
    }),
    Bold.configure({
      HTMLAttributes: {
        class: 'rt-bold',
      },
    }),
    // lists
    BulletList.configure({
      HTMLAttributes: {
        class: 'rt-bullet-list',
      },
    }),
    OrderedList.configure({
      HTMLAttributes: {
        class: 'rt-ordered-list',
      },
    }),
    ListItem.configure({
      HTMLAttributes: {
        class: 'rt-list-item',
      },
    }),
    TaskList.configure({
      HTMLAttributes: { class: 'rt-task-list' },
    }),
    TaskItem.configure({
      HTMLAttributes: {
        class: 'rt-task-item',
      },
      nested: true,
    }),
    Blockquote.configure({
      HTMLAttributes: {
        // class: 'border-dark-border-solid pl-3 rounded-l-sm',
        class: 'rt-blockquote',
        style: 'quotes: none',
      },
    }),
    CodeMark.configure({
      HTMLAttributes: {
        class: 'rt-code',
      },
    }),
    Kbd.configure({
      HTMLAttributes: {
        class: 'rt-kbd',
      },
    }), // patch to fix horizontal rule bug: https://github.com/ueberdosis/tiptap/pull/3859#issuecomment-1536799740
    Placeholder.configure({
      placeholder: () => {
        return placeholder ?? `Reply`
      },
      includeChildren: true,
    }),
    CustomVideo.configure({
      HTMLAttributes: {
        style: `width: ${mediaSize?.width}px; height: ${mediaSize?.height}px; object-fit: cover; border-radius: 4px;`,
      },
    }),
    Typography,
    Link.configure({
      HTMLAttributes: {
        class: 'rt-link',
      },
    }),
    CustomImage.configure({
      allowBase64: true,
      HTMLAttributes: {
        ...(mediaSize
          ? {
              style: `width: ${mediaSize?.width}px; height: ${mediaSize?.height}px; object-fit: cover; border-radius: 4px; cursor: pointer;`,
            }
          : {
              style: 'cursor: pointer;',
            }),
      },
    }),
    FakeCursor,
    Markdown,
    KeyboardShortcutHandler.extend({
      addKeyboardShortcuts() {
        return {
          Enter: ({ editor }) => {
            const isCodeBlockActive = editor.isActive('codeBlock')
            if (isCodeBlockActive) {
              return false
            }

            handleEnter({ editor })
            return editor.commands.clearContent()
          },

          'Mod-Enter': ({ editor }) => {
            /**
             * when inside of a codeblock and setting for sending the message with CMD/CTRL-Enter
             * force calling the `onSubmit` function and clear the editor content
             */

            handleEnter({ editor })
            return editor.commands.clearContent()
          },

          'Shift-Enter': ({ editor }) => {
            /**
             * currently we do not have an option to show a soft line break in the posts, so we overwrite
             * the behavior from tiptap with the default behavior on pressing enter
             */
            return editor.commands.first(({ commands }) => [
              () => commands.newlineInCode(),
              () => commands.splitListItem('listItem'), // This line added
              () => commands.createParagraphNear(),
              () => commands.liftEmptyBlock(),
              () => commands.splitBlock(),
            ])
          },
          Escape: ({ editor }) => {
            if (handleEscape) {
              handleEscape({ editor })
              return true
            }
            return false
          },
        }
      },
    }),
    Mention.configure({
      suggestion: {
        items: ({ query }: { query: string | null }) => {
          const items = getUsers(query)
          return items
        },
      },
      HTMLAttributes: mentionHTMLAttributes,
    }),
    BlockMention.configure({
      suggestion: {
        items: ({ query }: { query: string | null }) => {
          const items = getBlocks(query)
          return items
        },
      },
      getNode,
    }),
    DeleteFirstEmptyParagraphExtension,
  ] satisfies Extensions
}

const CustomImage = UpdatedImage.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
      },
      height: {
        default: null,
      },
    }
  },
  addProseMirrorPlugins() {
    return [UploadImagesPlugin(), handleImageClickPlugin()]
  },
})

const CustomVideo = Video.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
      },
      height: {
        default: null,
      },
    }
  },

  addProseMirrorPlugins() {
    return [UploadVideosPlugin()]
  },
})

const KeyboardShortcutHandler = Extension.create({
  name: 'keyboardHandler',
})
